import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import api from "../api/index";
import { ENDPOINTS } from "../api/endpoints";
import {
  formatPhoneWithPrefix,
  isValidKenyanPhone,
  normalizeKenyanPhone,
  KENYA_PHONE_ERROR,
  KENYA_PHONE_HINT,
  KENYA_PHONE_PLACEHOLDER,
  KENYA_PHONE_PREFIX,
} from "../utils/phone";

const initialForm = {
  name: "",
  email: "",
  phone: KENYA_PHONE_PREFIX,
  subject: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [inquiryNumber, setInquiryNumber] = useState("");
  const [error, setError] = useState("");

  const set = (field) => (e) => {
    const value =
      field === "phone" ? formatPhoneWithPrefix(e.target.value) : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.phone && form.phone !== KENYA_PHONE_PREFIX && !isValidKenyanPhone(form.phone)) {
      setError(KENYA_PHONE_ERROR);
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        ...form,
        phone:
          form.phone && form.phone !== KENYA_PHONE_PREFIX
            ? normalizeKenyanPhone(form.phone)
            : "",
      };
      const res = await api.post(ENDPOINTS.CONTACT.SUBMIT, payload);
      if (res.data?.isOk) {
        setSubmitted(true);
        setInquiryNumber(res.data.data?.inquiryNumber || "");
        setForm(initialForm);
      } else {
        setError(res.data?.message || "Failed to send message.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-[10px] pb-16 bg-card min-h-screen">
      <Helmet>
        <title>Contact Us</title>
        <meta
          name="description"
          content="Get in touch with Fonexpress Kenya. We're here to help with orders, products, and support."
        />
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-wide uppercase mb-3">
            Contact Us
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
            We'd love to hear from you
          </h1>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            Questions about an order, a product, or a partnership? Send us a message and our team will respond within 1–2 business days.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-background p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Email</h3>
                  <p className="text-sm text-muted-foreground mt-1">info@fonexpress.co.ke</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-background p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Phone className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Phone</h3>
                  <p className="text-sm text-muted-foreground mt-1">+254 700 000 000</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-background p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <MapPin className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Visit a store</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Find your nearest Fonexpress location on our{" "}
                    <Link to="/store-locator/" className="text-primary hover:underline">
                      store locator
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-border bg-background p-6 sm:p-8">
              {submitted ? (
                <div className="text-center py-10">
                  <CheckCircle2 className="h-14 w-14 text-success mx-auto mb-4" />
                  <h2 className="text-xl font-bold text-foreground">Message sent!</h2>
                  <p className="mt-2 text-muted-foreground">
                    Thank you for reaching out. Our team will get back to you soon.
                  </p>
                  {inquiryNumber && (
                    <p className="mt-3 text-sm text-muted-foreground">
                      Reference: <span className="font-mono text-foreground">{inquiryNumber}</span>
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="mt-6 text-sm text-primary hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        Full name <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={set("name")}
                        className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="John Kamau"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        Email <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={set("email")}
                        className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="you@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Phone</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={set("phone")}
                        className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder={KENYA_PHONE_PLACEHOLDER}
                      />
                      <p className="text-xs text-muted-foreground mt-1">{KENYA_PHONE_HINT}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        Subject <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={form.subject}
                        onChange={set("subject")}
                        className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="Order enquiry, product question..."
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Message <span className="text-destructive">*</span>
                    </label>
                    <textarea
                      required
                      rows={5}
                      value={form.message}
                      onChange={set("message")}
                      minLength={10}
                      className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                      placeholder="Tell us how we can help..."
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-destructive">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-brand text-primary-foreground text-sm font-semibold hover:shadow-glow-cyan transition-shadow disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Send message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
